import React, { useEffect, useState } from 'react';
import './App.css';

function SupportPage() {
    // Mobile detection
    const [isMobile, setIsMobile] = useState(false);
    
    // FAQ toggle states
    const [openFAQ, setOpenFAQ] = useState(null);

    // Check for mobile on mount
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const toggleFAQ = (index) => {
        setOpenFAQ(openFAQ === index ? null : index);
    };

    const faqs = [
        {
            question: "How do I install the Carrie.ai add-in?",
            answer: "Download the manifest.xml file from our install page, then copy it to your PowerPoint add-ins folder. For Mac: /Users/<username>/Library/Containers/com.microsoft.Powerpoint/Data/Documents/wef. For Windows: %APPDATA%\\Microsoft\\Office\\16.0\\Wef. Restart PowerPoint and access Carrie.ai from Home → Add-ins."
        },
        {
            question: "The add-in isn't showing up in PowerPoint. What should I do?",
            answer: "First, ensure you've placed the manifest.xml file in the correct folder and restarted PowerPoint. Check that you're using a supported version of PowerPoint (2016 or later). If the issue persists, try creating the 'wef' folder manually if it doesn't exist, and verify the manifest file isn't corrupted by re-downloading it."
        },
        {
            question: "Which versions of PowerPoint are supported?",
            answer: "Carrie.ai supports PowerPoint 2016, 2019, 2021, and Microsoft 365 on both Windows and Mac. The add-in requires an internet connection to function as it uses cloud-based AI services."
        },
        {
            question: "Is my presentation data secure?",
            answer: "Yes, we take data security seriously. All data is encrypted in transit and at rest. We use enterprise-grade security measures and only process your data to provide the AI functionality. We don't store your presentation content permanently unless necessary for troubleshooting."
        },
        {
            question: "How do I use Carrie.ai to edit my slides?",
            answer: "Once installed, open any PowerPoint presentation and go to Home → Add-ins → Carrie.ai. You can then type natural language commands like 'Make this text larger' or 'Change the color scheme to blue' and Carrie.ai will make the changes for you."
        },
        {
            question: "The add-in is slow or not responding. How can I fix this?",
            answer: "Slow performance is usually due to internet connectivity issues. Check your internet connection and try again. If the problem persists, try closing and reopening PowerPoint, or restart your computer. Large presentations may take longer to process."
        },
        {
            question: "Can I use Carrie.ai offline?",
            answer: "No, Carrie.ai requires an internet connection to function as it uses cloud-based AI services to process your requests and make intelligent edits to your presentations."
        },
        {
            question: "How do I uninstall the add-in?",
            answer: "To uninstall Carrie.ai, simply delete the manifest.xml file from your PowerPoint add-ins folder and restart PowerPoint. The add-in will no longer appear in your Add-ins menu."
        },
        {
            question: "I'm getting error messages. What should I do?",
            answer: "Error messages usually indicate connectivity issues or problems with the AI service. Try refreshing the add-in or restarting PowerPoint. If errors persist, please contact our support team with the specific error message and we'll help you resolve it."
        },
        {
            question: "Is there a limit to how much I can use Carrie.ai?",
            answer: "During the beta period, there may be usage limits to ensure fair access for all users. We'll notify users of any limits and provide information about premium plans as they become available."
        }
    ];

    return (
        <div style={{
            minHeight: '100vh',
            width: '100vw',
            display: 'flex',
            flexDirection: 'column',
            background: 'none',
            position: 'relative'
        }}>
            {/* Top Bar */}
            <div style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: isMobile ? '24px 24px 0 24px' : '32px 48px 0 48px',
                boxSizing: 'border-box'
            }}>
                {/* Logo */}
                <div style={{
                    fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                    fontWeight: 700,
                    fontSize: isMobile ? '1.5rem' : '2rem',
                    color: '#222',
                    letterSpacing: '-0.02em'
                }}>
                    Carrie.ai
                </div>
                {/* Nav */}
                <div style={{
                    display: 'flex',
                    gap: isMobile ? 16 : 32,
                    fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                    fontWeight: 500,
                    fontSize: isMobile ? '0.9rem' : '1.05rem'
                }}>
                    <a href="/" style={{ color: '#222', textDecoration: 'none', opacity: 0.7 }}>Home</a>
                    <a href="/Demo" style={{ color: '#222', textDecoration: 'none', opacity: 0.7 }}>Demo</a>
                    <a href="/privacy" style={{ color: '#222', textDecoration: 'none', opacity: 0.7 }}>Privacy</a>
                </div>
            </div>

            {/* Main Content */}
            <main style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                marginTop: isMobile ? 32 : 64,
                paddingBottom: isMobile ? 40 : 80,
                padding: isMobile ? '0 24px' : '0 48px'
            }}>
                {/* Header */}
                <div style={{
                    width: '100%',
                    maxWidth: 900,
                    marginBottom: isMobile ? 40 : 60,
                    textAlign: 'center'
                }}>
                    <h1 style={{
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 700,
                        fontSize: isMobile ? '2.5rem' : '3.5rem',
                        color: '#181c2a',
                        textAlign: 'center',
                        margin: '0 0 16px 0',
                        letterSpacing: '-0.02em',
                        lineHeight: 1.2
                    }}>
                        Support Center
                    </h1>
                    <p style={{
                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                        fontWeight: 400,
                        fontSize: isMobile ? '1.1rem' : '1.25rem',
                        color: '#666',
                        textAlign: 'center',
                        margin: 0,
                        lineHeight: 1.5,
                        maxWidth: 600,
                        marginLeft: 'auto',
                        marginRight: 'auto'
                    }}>
                        Get help with installing and using your Carrie.ai PowerPoint add-in
                    </p>
                </div>

                {/* Quick Help Cards */}
                <div style={{
                    width: '100%',
                    maxWidth: 900,
                    marginBottom: isMobile ? 40 : 60
                }}>
                    <h2 style={{
                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                        fontWeight: 600,
                        fontSize: isMobile ? '1.5rem' : '1.8rem',
                        color: '#181c2a',
                        textAlign: 'center',
                        margin: '0 0 32px 0'
                    }}>
                        Quick Help
                    </h2>
                    
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: isMobile ? 20 : 24,
                    }}>
                        {/* Installation Help */}
                        <div style={{
                            background: 'white',
                            borderRadius: 16,
                            padding: isMobile ? '24px 20px' : '32px 24px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                            border: '1px solid rgba(0,0,0,0.08)',
                            textAlign: 'center',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}>
                            <div style={{
                                fontSize: '2.5rem',
                                marginBottom: 16
                            }}>📥</div>
                            <h3 style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 600,
                                fontSize: isMobile ? '1.1rem' : '1.2rem',
                                color: '#181c2a',
                                margin: '0 0 12px 0'
                            }}>
                                Installation Guide
                            </h3>
                            <p style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 400,
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                color: '#555',
                                margin: '0 0 20px 0',
                                lineHeight: 1.5
                            }}>
                                Book a Demo to learn more
                            </p>
                            <a 
                                href="/Demo" 
                                style={{
                                    display: 'inline-block',
                                    padding: '10px 20px',
                                    background: '#7f6fff',
                                    color: 'white',
                                    textDecoration: 'none',
                                    borderRadius: 8,
                                    fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#6b5ce6'}
                                onMouseLeave={(e) => e.target.style.background = '#7f6fff'}
                            >
                                View Guide
                            </a>
                        </div>

                        {/* Troubleshooting */}
                        <div style={{
                            background: 'white',
                            borderRadius: 16,
                            padding: isMobile ? '24px 20px' : '32px 24px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                            border: '1px solid rgba(0,0,0,0.08)',
                            textAlign: 'center',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}>
                            <div style={{
                                fontSize: '2.5rem',
                                marginBottom: 16
                            }}>🔧</div>
                            <h3 style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 600,
                                fontSize: isMobile ? '1.1rem' : '1.2rem',
                                color: '#181c2a',
                                margin: '0 0 12px 0'
                            }}>
                                Troubleshooting
                            </h3>
                            <p style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 400,
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                color: '#555',
                                margin: '0 0 20px 0',
                                lineHeight: 1.5
                            }}>
                                Common issues and solutions for the Carrie.ai add-in
                            </p>
                            <a 
                                href="#troubleshooting" 
                                style={{
                                    display: 'inline-block',
                                    padding: '10px 20px',
                                    background: '#34c759',
                                    color: 'white',
                                    textDecoration: 'none',
                                    borderRadius: 8,
                                    fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#28a745'}
                                onMouseLeave={(e) => e.target.style.background = '#34c759'}
                            >
                                Get Help
                            </a>
                        </div>

                        {/* Contact Support */}
                        <div style={{
                            background: 'white',
                            borderRadius: 16,
                            padding: isMobile ? '24px 20px' : '32px 24px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                            border: '1px solid rgba(0,0,0,0.08)',
                            textAlign: 'center',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}>
                            <div style={{
                                fontSize: '2.5rem',
                                marginBottom: 16
                            }}>💬</div>
                            <h3 style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 600,
                                fontSize: isMobile ? '1.1rem' : '1.2rem',
                                color: '#181c2a',
                                margin: '0 0 12px 0'
                            }}>
                                Contact Support
                            </h3>
                            <p style={{
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 400,
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                color: '#555',
                                margin: '0 0 20px 0',
                                lineHeight: 1.5
                            }}>
                                Get personalized help from our support team
                            </p>
                            <a 
                                href="#contact" 
                                style={{
                                    display: 'inline-block',
                                    padding: '10px 20px',
                                    background: '#ff6b6b',
                                    color: 'white',
                                    textDecoration: 'none',
                                    borderRadius: 8,
                                    fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#ff5252'}
                                onMouseLeave={(e) => e.target.style.background = '#ff6b6b'}
                            >
                                Contact Us
                            </a>
                        </div>
                    </div>
                </div>

                {/* FAQ Section */}
                <div id="troubleshooting" style={{
                    width: '100%',
                    maxWidth: 800,
                    marginBottom: isMobile ? 40 : 60
                }}>
                    <h2 style={{
                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                        fontWeight: 600,
                        fontSize: isMobile ? '1.5rem' : '1.8rem',
                        color: '#181c2a',
                        textAlign: 'center',
                        margin: '0 0 32px 0'
                    }}>
                        Frequently Asked Questions
                    </h2>
                    
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16
                    }}>
                        {faqs.map((faq, index) => (
                            <div 
                                key={index}
                                style={{
                                    background: 'white',
                                    borderRadius: 12,
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                }}
                            >
                                <button
                                    onClick={() => toggleFAQ(index)}
                                    style={{
                                        width: '100%',
                                        padding: isMobile ? '16px 20px' : '20px 24px',
                                        background: 'none',
                                        border: 'none',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                        fontWeight: 500,
                                        fontSize: isMobile ? '1rem' : '1.1rem',
                                        color: '#181c2a',
                                        transition: 'background 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                                    onMouseLeave={(e) => e.target.style.background = 'none'}
                                >
                                    <span>{faq.question}</span>
                                    <span style={{
                                        fontSize: '1.2rem',
                                        transform: openFAQ === index ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease'
                                    }}>
                                        ▼
                                    </span>
                                </button>
                                {openFAQ === index && (
                                    <div style={{
                                        padding: isMobile ? '0 20px 20px 20px' : '0 24px 24px 24px',
                                        borderTop: '1px solid rgba(0,0,0,0.06)',
                                        background: '#f8f9fa'
                                    }}>
                                        <p style={{
                                            fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                            fontWeight: 400,
                                            fontSize: isMobile ? '0.9rem' : '1rem',
                                            color: '#555',
                                            margin: 0,
                                            lineHeight: 1.6
                                        }}>
                                            {faq.answer}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* System Requirements */}
                <div style={{
                    width: '100%',
                    maxWidth: 800,
                    marginBottom: isMobile ? 40 : 60,
                    background: 'rgba(127, 111, 255, 0.05)',
                    borderRadius: 16,
                    padding: isMobile ? '24px 20px' : '32px 24px',
                    border: '1px solid rgba(127, 111, 255, 0.1)'
                }}>
                    <h2 style={{
                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                        fontWeight: 600,
                        fontSize: isMobile ? '1.3rem' : '1.5rem',
                        color: '#181c2a',
                        margin: '0 0 20px 0',
                        textAlign: 'center'
                    }}>
                        System Requirements
                    </h2>
                    
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                        gap: isMobile ? 20 : 32,
                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                        fontSize: isMobile ? '0.9rem' : '1rem',
                        color: '#444'
                    }}>
                        <div>
                            <h3 style={{
                                fontWeight: 600,
                                fontSize: isMobile ? '1.1rem' : '1.2rem',
                                color: '#181c2a',
                                margin: '0 0 12px 0'
                            }}>
                                Windows
                            </h3>
                            <ul style={{ paddingLeft: 20, margin: 0 }}>
                                <li style={{ marginBottom: 8 }}>PowerPoint 2016 or later</li>
                                <li style={{ marginBottom: 8 }}>Windows 10 or later</li>
                                <li style={{ marginBottom: 8 }}>Internet connection required</li>
                                <li style={{ marginBottom: 8 }}>Microsoft 365 subscription (recommended)</li>
                            </ul>
                        </div>
                        
                        <div>
                            <h3 style={{
                                fontWeight: 600,
                                fontSize: isMobile ? '1.1rem' : '1.2rem',
                                color: '#181c2a',
                                margin: '0 0 12px 0'
                            }}>
                                Mac
                            </h3>
                            <ul style={{ paddingLeft: 20, margin: 0 }}>
                                <li style={{ marginBottom: 8 }}>PowerPoint 2016 or later</li>
                                <li style={{ marginBottom: 8 }}>macOS 10.14 or later</li>
                                <li style={{ marginBottom: 8 }}>Internet connection required</li>
                                <li style={{ marginBottom: 8 }}>Microsoft 365 subscription (recommended)</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Contact Section */}
                <div id="contact" style={{
                    width: '100%',
                    maxWidth: 800,
                    marginBottom: isMobile ? 40 : 60
                }}>
                    <h2 style={{
                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                        fontWeight: 600,
                        fontSize: isMobile ? '1.5rem' : '1.8rem',
                        color: '#181c2a',
                        textAlign: 'center',
                        margin: '0 0 16px 0'
                    }}>
                        Still Need Help?
                    </h2>
                    
                    <p style={{
                        fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                        fontWeight: 400,
                        fontSize: isMobile ? '1rem' : '1.1rem',
                        color: '#666',
                        textAlign: 'center',
                        margin: '0 0 32px 0',
                        lineHeight: 1.5
                    }}>
                        Our support team is here to help you get the most out of Carrie.ai
                    </p>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: isMobile ? 16 : 24,
                    }}>
                        {/* Email Support */}
                        <a 
                            href="mailto:wafi@utexas.edu?subject=Carrie.ai Support Request" 
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                padding: isMobile ? '16px' : '20px',
                                background: 'white',
                                borderRadius: 12,
                                textDecoration: 'none',
                                color: '#444',
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 500,
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                transition: 'all 0.2s ease',
                                border: '1px solid #e9ecef',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#7f6fff';
                                e.target.style.color = '#fff';
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 4px 16px rgba(127, 111, 255, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'white';
                                e.target.style.color = '#444';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                            }}
                        >
                            <span style={{ fontSize: '1.5rem' }}>📧</span>
                            <div>
                                <div style={{ fontWeight: 600 }}>Email Support</div>
                                <div style={{ fontSize: '0.85em', opacity: 0.8 }}>wafi@utexas.edu</div>
                            </div>
                        </a>

                        {/* Phone Support */}
                        <a 
                            href="tel:+14695446042" 
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                padding: isMobile ? '16px' : '20px',
                                background: 'white',
                                borderRadius: 12,
                                textDecoration: 'none',
                                color: '#444',
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 500,
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                transition: 'all 0.2s ease',
                                border: '1px solid #e9ecef',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#34c759';
                                e.target.style.color = '#fff';
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 4px 16px rgba(52, 199, 89, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'white';
                                e.target.style.color = '#444';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                            }}
                        >
                            <span style={{ fontSize: '1.5rem' }}>📱</span>
                            <div>
                                <div style={{ fontWeight: 600 }}>Phone Support</div>
                                <div style={{ fontSize: '0.85em', opacity: 0.8 }}>(469) 544-6042</div>
                            </div>
                        </a>

                        {/* Twitter Support */}
                        <a 
                            href="https://twitter.com/wafitoffy_" 
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                padding: isMobile ? '16px' : '20px',
                                background: 'white',
                                borderRadius: 12,
                                textDecoration: 'none',
                                color: '#444',
                                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                                fontWeight: 500,
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                transition: 'all 0.2s ease',
                                border: '1px solid #e9ecef',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#1da1f2';
                                e.target.style.color = '#fff';
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 4px 16px rgba(29, 161, 242, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'white';
                                e.target.style.color = '#444';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                            }}
                        >
                            <span style={{ fontSize: '1.5rem' }}>🐦</span>
                            <div>
                                <div style={{ fontWeight: 600 }}>Social Support</div>
                                <div style={{ fontSize: '0.85em', opacity: 0.8 }}>@wafitoffy_</div>
                            </div>
                        </a>
                    </div>

                    {/* Response Time Info */}
                    <div style={{
                        marginTop: 32,
                        padding: isMobile ? '16px' : '20px',
                        background: '#f8f9fa',
                        borderRadius: 12,
                        border: '1px solid #e9ecef',
                        textAlign: 'center'
                    }}>
                        <p style={{
                            fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                            fontWeight: 500,
                            fontSize: isMobile ? '0.9rem' : '1rem',
                            color: '#666',
                            margin: '0 0 8px 0'
                        }}>
                            📞 <strong>Response Times:</strong>
                        </p>
                        <p style={{
                            fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                            fontWeight: 400,
                            fontSize: isMobile ? '0.85rem' : '0.95rem',
                            color: '#888',
                            margin: 0,
                            lineHeight: 1.4
                        }}>
                            Email: Within 24 hours • Phone: Business hours (9 AM - 6 PM CT) • Twitter: Within 4 hours
                        </p>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer style={{
                width: '100%',
                padding: isMobile ? '32px 0 24px 0' : '48px 0 32px 0',
                textAlign: 'center',
                fontFamily: "'Neue Haas Grotesk Display Pro', sans-serif",
                fontWeight: 400,
                fontSize: isMobile ? '0.85rem' : '0.95rem',
                color: '#888',
                borderTop: '1px solid #eee',
                marginTop: 40
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: isMobile ? 16 : 32,
                    marginBottom: 16,
                    flexWrap: 'wrap'
                }}>
                    <a href="/" style={{ color: '#888', textDecoration: 'none' }}>Home</a>
                    <a href="/demo" style={{ color: '#888', textDecoration: 'none' }}>Demo</a>
                    <a href="/privacy" style={{ color: '#888', textDecoration: 'none' }}>Privacy Policy</a>
                    <a href="/support" style={{ color: '#7f6fff', textDecoration: 'none' }}>Support</a>
                </div>
                <p style={{ margin: 0 }}>
                    © {new Date().getFullYear()} Carrie.ai. All rights reserved.
                </p>
            </footer>
        </div>
    );
}

export default SupportPage; 